import { t, tn, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ClientContractRow } from '../../services/clientService';
import {
  siteService,
  type LicenceTypeRow,
  type SiteGeofenceMode,
  type SiteRow,
  type SiteWelfareMode,
} from '../../services/siteService';
import type { PlaceDetails } from '../../services/googlePlacesService';
import { sanitizeNumber, sanitizeText } from '../../utils/sanitize';
import {
  parseGeometryPoint,
  parseGeometryPolygon,
  type LatLngTuple,
} from '../../utils/geo';
import {
  COVERAGE_END_OPTIONS,
  DEFAULT_COVERAGE_END_MINUTE,
  DEFAULT_COVERAGE_START_MINUTE,
  MAX_REQUIRED_GUARDS,
  MINUTES_PER_DAY,
  TIME_SELECT_OPTIONS,
  countryRequiresTimeZoneSelection,
  formatCoverageWindow,
  formatTimeOptionLabel,
  getCoverageDurationMinutes,
  getDefaultTimeZoneForCountry,
  getSiteCoverageWindow,
  getTimeZoneOptionsForCountry,
  normaliseCoverageWindow,
  resolveSiteTimeZone,
  resolveTimeZoneForCountrySelection,
} from '../../utils/siteScheduling';
import {
  DEFAULT_SITE_GEOFENCE_RADIUS_M,
  DEFAULT_WELFARE_NIGHT_END_MINUTE,
  DEFAULT_WELFARE_NIGHT_START_MINUTE,
  MAX_SITE_GEOFENCE_RADIUS_M,
  MIN_SITE_GEOFENCE_RADIUS_M,
} from './siteGeofence';
import { filterCountries, getCountryName, getFlagSrc } from './countries';
import AddressSearch from './AddressSearch';
import MapPickerModal from './MapPickerModal';
import SiteBoundaryCanvas from './SiteBoundaryCanvas';
import logoSrc from '../../assets/Logo_Master.svg';
import './SiteBuilder.css';

type StepId = 'location' | 'boundary' | 'coverage' | 'duty' | 'review';

/** Steps a caller can open the builder on. */
export type SiteBuilderStep = StepId;

const STEP_IDS: StepId[] = ['location', 'boundary', 'coverage', 'duty', 'review'];
const MIN_POLYGON_VERTICES = 3;

/** Written as literal t() calls so the i18n scanner can find every label. */
function getStepLabel(step: StepId): string {
  switch (step) {
    case 'location': return t('Location');
    case 'boundary': return t('Boundary');
    case 'coverage': return t('Coverage');
    case 'duty': return t('Duty rules');
    default: return t('Review');
  }
}

function getStepTitle(step: StepId, isEditing: boolean): string {
  switch (step) {
    case 'location': return t('Where is this site?');
    case 'boundary': return t('Draw the site boundary');
    case 'coverage': return t('When does this site need cover?');
    case 'duty': return t('Duty rules for this site');
    default: return isEditing ? t('Review your changes') : t('Review and create');
  }
}

function getStepHint(step: StepId, isEditing: boolean): string {
  switch (step) {
    case 'location': return t('Name the site and pin it on the map. Everything else follows from this.');
    case 'boundary': return t('Guards are judged in or out of this area, so keep it tight to the real perimeter.');
    case 'coverage': return t('Duty board slots can only be created inside this window.');
    case 'duty': return t('Defaults for every shift here. Individual shifts can still override them.');
    default: return isEditing
      ? t('Check the details, then save your changes.')
      : t('Check the details, then create the site.');
  }
}

/** A site keeps the country it was saved with. A new one -- or a legacy site
 *  saved without one -- starts in its client's country. */
function getInitialCountryCode(site: SiteRow | null, client: ClientContractRow | null): string | null {
  return site?.country_code?.trim() || client?.country_code?.trim() || null;
}

interface SiteBuilderProps {
  client: ClientContractRow | null;
  /** Present for an edit; absent creates a new site under `client`. */
  site?: SiteRow | null;
  initialStep?: StepId;
  onClose: () => void;
  onSaved: (siteName: string) => void;
}

export default function SiteBuilder({
  client,
  site = null,
  initialStep,
  onClose,
  onSaved,
}: SiteBuilderProps) {
  const { t: translate, tn: translatePlural } = useI18n();
  const isEditing = site != null;

  const [stepIndex, setStepIndex] = useState(() => {
    const index = initialStep ? STEP_IDS.indexOf(initialStep) : 0;
    return index >= 0 ? index : 0;
  });
  // Everything is already filled in when editing, so no step is gated.
  const [furthestStepIndex, setFurthestStepIndex] = useState(
    () => (site ? STEP_IDS.length - 1 : 0),
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exitConfirmPending, setExitConfirmPending] = useState(false);

  // Step 1 -- identity and location
  const [name, setName] = useState(() => site?.name ?? '');
  const [address, setAddress] = useState(() => site?.address ?? '');
  const [latitude, setLatitude] = useState<number | null>(
    () => parseGeometryPoint(site?.location)?.[0] ?? null,
  );
  const [longitude, setLongitude] = useState<number | null>(
    () => parseGeometryPoint(site?.location)?.[1] ?? null,
  );
  const [placeId, setPlaceId] = useState<string | null>(() => site?.place_id ?? null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [countryCode, setCountryCode] = useState<string | null>(
    () => getInitialCountryCode(site, client),
  );
  const [timezone, setTimezone] = useState(() => (
    site ? resolveSiteTimeZone(site) : getDefaultTimeZoneForCountry(getInitialCountryCode(site, client))
  ));
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countryActiveIndex, setCountryActiveIndex] = useState(-1);

  // Step 2 -- geofence
  const [geofenceMode, setGeofenceMode] = useState<SiteGeofenceMode>(() => (
    site?.geofence_type === 'polygon' ? 'polygon' : 'radius'
  ));
  const [geofenceRadius, setGeofenceRadius] = useState(() => String(
    site?.geofence_radius_m ?? site?.attendance_radius_m ?? DEFAULT_SITE_GEOFENCE_RADIUS_M,
  ));
  const [geofencePolygonPoints, setGeofencePolygonPoints] = useState<LatLngTuple[]>(
    () => parseGeometryPolygon(site?.geofence_polygon) ?? [],
  );

  // Step 3 -- coverage window
  const [is24HourCoverage, setIs24HourCoverage] = useState(() => (
    !site || getCoverageDurationMinutes(getSiteCoverageWindow(site)) >= MINUTES_PER_DAY
  ));
  const [coverageStartMinute, setCoverageStartMinute] = useState(
    () => getSiteCoverageWindow(site).startMinute,
  );
  const [coverageEndMinute, setCoverageEndMinute] = useState(
    () => getSiteCoverageWindow(site).endMinute,
  );
  // Text, not a number, so the field can be cleared while typing.
  const [requiredGuardCount, setRequiredGuardCount] = useState(
    () => String(site?.required_guard_count ?? 1),
  );

  // Step 4 -- duty rules
  const [welfareCheckMode, setWelfareCheckMode] = useState<SiteWelfareMode>(
    () => site?.welfare_check_mode ?? 'automatic',
  );
  const [welfareNightStartMinute, setWelfareNightStartMinute] = useState(
    () => site?.welfare_night_start_minute ?? DEFAULT_WELFARE_NIGHT_START_MINUTE,
  );
  const [welfareNightEndMinute, setWelfareNightEndMinute] = useState(
    () => site?.welfare_night_end_minute ?? DEFAULT_WELFARE_NIGHT_END_MINUTE,
  );
  const [licenceTypes, setLicenceTypes] = useState<LicenceTypeRow[]>([]);
  const [requiredLicenceTypeId, setRequiredLicenceTypeId] = useState(
    () => site?.required_licence_type_id ?? '',
  );
  const [isLoadingLicenceTypes, setIsLoadingLicenceTypes] = useState(false);

  const countryContainerRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const hasRenderedRef = useRef(false);

  const step = STEP_IDS[stepIndex];
  const isMapStep = step === 'boundary';

  const siteCenter = useMemo<LatLngTuple | null>(() => (
    latitude != null && longitude != null ? [latitude, longitude] : null
  ), [latitude, longitude]);

  const timeZoneOptions = useMemo(() => getTimeZoneOptionsForCountry(countryCode), [countryCode]);
  const requiresTimeZoneSelection = useMemo(
    () => countryRequiresTimeZoneSelection(countryCode),
    [countryCode],
  );
  const resolvedTimeZone = useMemo(
    () => resolveTimeZoneForCountrySelection(countryCode, timezone),
    [countryCode, timezone],
  );
  const filteredCountries = useMemo(() => filterCountries(countrySearch), [countrySearch]);

  const coveragePreview = useMemo(() => (
    is24HourCoverage
      ? translate('24 hours')
      : formatCoverageWindow({
        required_coverage_start_minute: coverageStartMinute,
        required_coverage_end_minute: coverageEndMinute,
      })
  ), [coverageEndMinute, coverageStartMinute, is24HourCoverage, translate]);

  const isOvernightCoverage = !is24HourCoverage && coverageEndMinute <= coverageStartMinute;

  const geofencePreview = useMemo(() => (
    geofenceMode === 'polygon'
      ? (
        geofencePolygonPoints.length >= MIN_POLYGON_VERTICES
          ? translatePlural('Site geofence bounds with {count} point', 'Site geofence bounds with {count} points', geofencePolygonPoints.length)
          : translatePlural('Site geofence bounds need {count} more point', 'Site geofence bounds need {count} more points', MIN_POLYGON_VERTICES - geofencePolygonPoints.length)
      )
      : translate('Radius {radius} m around the site location', { radius: geofenceRadius || DEFAULT_SITE_GEOFENCE_RADIUS_M })
  ), [geofenceMode, geofencePolygonPoints.length, geofenceRadius, translate, translatePlural]);

  const welfarePreview = useMemo(() => {
    if (welfareCheckMode === 'always') return translate('Always on for every shift');
    if (welfareCheckMode === 'off') return translate('Off by default');
    return translate('Automatic (alone or {start}-{end})', {
      start: formatTimeOptionLabel(welfareNightStartMinute),
      end: formatTimeOptionLabel(welfareNightEndMinute),
    });
  }, [translate, welfareCheckMode, welfareNightEndMinute, welfareNightStartMinute]);

  const draft = useMemo(() => ({
    name: name.trim(),
    address: address.trim(),
    latitude,
    longitude,
    placeId,
    countryCode,
    timezone: resolvedTimeZone,
    is24HourCoverage,
    coverageStartMinute,
    coverageEndMinute,
    requiredGuardCount,
    welfareCheckMode,
    welfareNightStartMinute,
    welfareNightEndMinute,
    requiredLicenceTypeId,
    geofenceMode,
    geofenceRadius,
    geofencePolygonPoints,
  }), [
    address, countryCode, coverageEndMinute, coverageStartMinute, geofenceMode,
    geofencePolygonPoints, geofenceRadius, is24HourCoverage, latitude, longitude,
    name, placeId, requiredGuardCount, requiredLicenceTypeId, resolvedTimeZone,
    welfareCheckMode, welfareNightEndMinute, welfareNightStartMinute,
  ]);

  const parsedGuardCount = /^\d+$/.test(requiredGuardCount.trim())
    ? Number.parseInt(requiredGuardCount.trim(), 10)
    : null;

  // Snapshot the opening state on the first render so leaving without a real
  // change never prompts, in either mode.
  const initialDraftRef = useRef<string | null>(null);
  if (initialDraftRef.current === null) initialDraftRef.current = JSON.stringify(draft);
  const isDirty = JSON.stringify(draft) !== initialDraftRef.current;

  // ── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    if (!hasRenderedRef.current) {
      hasRenderedRef.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [stepIndex]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (countryContainerRef.current && !countryContainerRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
        setCountryActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  useEffect(() => {
    if (!countryCode) {
      setLicenceTypes([]);
      setRequiredLicenceTypeId('');
      setIsLoadingLicenceTypes(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingLicenceTypes(true);

    void siteService.fetchLicenceTypes(countryCode)
      .then((rows) => {
        if (cancelled) return;
        setLicenceTypes(rows);
        setRequiredLicenceTypeId((prev) => (rows.some((row) => row.id === prev) ? prev : ''));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading licence types:', (err as Error).message);
        setLicenceTypes([]);
        setRequiredLicenceTypeId('');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLicenceTypes(false);
      });

    return () => { cancelled = true; };
  }, [countryCode]);

  // ── Validation and navigation ───────────────────────────────────────

  const validateStep = useCallback((stepId: StepId): string | null => {
    switch (stepId) {
      case 'location':
        if (!sanitizeText(name, 100)) return t('Site name is required');
        return null;
      case 'boundary':
        if (geofenceMode === 'radius' && !siteCenter) {
          return t('Set the site location before saving a radius geofence.');
        }
        if (geofenceMode === 'polygon' && geofencePolygonPoints.length < MIN_POLYGON_VERTICES) {
          return t('Add at least three points to define the site geofence bounds.');
        }
        return null;
      case 'coverage':
        if (!is24HourCoverage && coverageStartMinute === coverageEndMinute) {
          return t('Coverage start and end cannot match unless the site is 24-hour coverage.');
        }
        if (parsedGuardCount === null || parsedGuardCount < 1 || parsedGuardCount > MAX_REQUIRED_GUARDS) {
          return t('Enter how many guards the site needs at once, from 1 to {max}.', { max: MAX_REQUIRED_GUARDS });
        }
        return null;
      case 'duty':
        if (welfareCheckMode === 'automatic' && welfareNightStartMinute === welfareNightEndMinute) {
          return t('Night start and end cannot be the same.');
        }
        return null;
      default:
        return null;
    }
  }, [
    coverageEndMinute, coverageStartMinute, geofenceMode, geofencePolygonPoints.length,
    is24HourCoverage, name, parsedGuardCount, siteCenter, welfareCheckMode,
    welfareNightEndMinute, welfareNightStartMinute,
  ]);

  const goToStep = useCallback((targetIndex: number) => {
    const clamped = Math.min(Math.max(targetIndex, 0), STEP_IDS.length - 1);
    // A new site is built up in order, so moving on confirms the step behind you.
    // An existing site is already valid on the server: navigate freely and let
    // Save do the checking, or a legacy row would trap you on one step.
    if (!isEditing && clamped > stepIndex) {
      for (let index = stepIndex; index < clamped; index += 1) {
        const stepError = validateStep(STEP_IDS[index]);
        if (stepError) {
          setStepIndex(index);
          setError(stepError);
          if (STEP_IDS[index] === 'location') nameInputRef.current?.focus();
          return;
        }
      }
    }
    setError('');
    setStepIndex(clamped);
    setFurthestStepIndex((current) => Math.max(current, clamped));
  }, [isEditing, stepIndex, validateStep]);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setExitConfirmPending(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showMapPicker || countryDropdownOpen) return;
      if (exitConfirmPending) {
        setExitConfirmPending(false);
        return;
      }
      handleRequestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [countryDropdownOpen, exitConfirmPending, handleRequestClose, showMapPicker]);

  // ── Location handlers ───────────────────────────────────────────────

  const handleCountryKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!countryDropdownOpen || !filteredCountries.length) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setCountryActiveIndex((i) => Math.min(i + 1, filteredCountries.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setCountryActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (event.key === 'Enter') {
      event.preventDefault();
      if (countryActiveIndex >= 0) applyCountry(filteredCountries[countryActiveIndex].code);
    } else if (event.key === 'Escape') { setCountryDropdownOpen(false); setCountryActiveIndex(-1); }
  };

  /** Licence types and timezones are per country, so a change resets both. */
  const applyCountry = (code: string) => {
    if (code !== countryCode) {
      setCountryCode(code);
      setTimezone(resolveTimeZoneForCountrySelection(code, null));
      setRequiredLicenceTypeId('');
    }
    setCountrySearch('');
    setCountryDropdownOpen(false);
    setCountryActiveIndex(-1);
  };

  const handlePlaceSelected = (place: PlaceDetails) => {
    setAddress(place.formattedAddress);
    setLatitude(place.latitude ?? null);
    setLongitude(place.longitude ?? null);
    setPlaceId(place.placeId);
    if (!name.trim() && place.name) setName(place.name);
    // The address is the ground truth for where the site is. With the search
    // limited to the chosen country this only fills a blank country in.
    if (place.countryCode) applyCountry(place.countryCode);
  };

  const handleMapConfirm = (lat: number, lng: number, resolvedAddress: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setAddress(resolvedAddress);
    setPlaceId(null);
    setShowMapPicker(false);
  };

  const handleSiteCenterChange = useCallback(([lat, lng]: LatLngTuple) => {
    setLatitude(lat);
    setLongitude(lng);
    setPlaceId(null);
  }, []);

  // ── Submit ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    const firstInvalid = STEP_IDS.findIndex((stepId) => validateStep(stepId) != null);
    if (firstInvalid >= 0) {
      setStepIndex(firstInvalid);
      setError(validateStep(STEP_IDS[firstInvalid]) ?? '');
      return;
    }
    if (!isEditing && !client) {
      setError(t('Create or select a client before adding a site'));
      return;
    }

    const cleanName = sanitizeText(name, 100);
    const cleanGeofenceRadius = sanitizeNumber(geofenceRadius, {
      min: MIN_SITE_GEOFENCE_RADIUS_M,
      max: MAX_SITE_GEOFENCE_RADIUS_M,
      integer: true,
      defaultValue: DEFAULT_SITE_GEOFENCE_RADIUS_M,
    }) ?? DEFAULT_SITE_GEOFENCE_RADIUS_M;

    const coverageWindow = is24HourCoverage
      ? { startMinute: DEFAULT_COVERAGE_START_MINUTE, endMinute: DEFAULT_COVERAGE_END_MINUTE }
      : normaliseCoverageWindow(coverageStartMinute, coverageEndMinute);

    const params = {
      name: cleanName,
      address,
      latitude,
      longitude,
      placeId,
      countryCode,
      timezone: resolvedTimeZone,
      coverageStartMinute: coverageWindow.startMinute,
      coverageEndMinute: coverageWindow.endMinute,
      requiredGuardCount: parsedGuardCount ?? 1,
      welfareCheckMode,
      welfareNightStartMinute,
      welfareNightEndMinute,
      requiredLicenceTypeId: requiredLicenceTypeId || null,
      geofenceMode,
      geofenceRadiusM: cleanGeofenceRadius,
      geofencePolygon: geofenceMode === 'polygon' ? geofencePolygonPoints : null,
    };

    setIsSubmitting(true);
    setError('');
    try {
      if (site) {
        await siteService.updateSite(site.id, params);
      } else {
        await siteService.createSite({ clientContractId: client?.id ?? null, ...params });
      }
      onSaved(cleanName);
    } catch (err) {
      setError((err as Error).message
        || (site ? t('Failed to update site') : t('Failed to create site')));
      setIsSubmitting(false);
    }
  };

  const handleStepSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (step === 'review') {
      void handleSave();
      return;
    }
    const stepError = isEditing ? null : validateStep(step);
    if (stepError) {
      setError(stepError);
      return;
    }
    goToStep(stepIndex + 1);
  };

  // ── Step bodies ─────────────────────────────────────────────────────

  const renderLocationStep = () => (
    <>
      <div className="sb-field">
        <label className="sb-field__label" htmlFor="site-name">{t('Site Name *')}</label>
        <input
          id="site-name"
          ref={nameInputRef}
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="sb-input"
          placeholder={t('e.g. Riverside Distribution Centre')}
          autoFocus
        />
      </div>

      <div className="sb-field sb-country" ref={countryContainerRef}>
        <span className="sb-field__label" id="site-country-label">{t('Country')}</span>
        <button
          type="button"
          className="sb-country__trigger"
          aria-labelledby="site-country-label site-country-value"
          aria-expanded={countryDropdownOpen}
          onClick={() => { setCountryDropdownOpen((open) => !open); setCountrySearch(''); setCountryActiveIndex(-1); }}
        >
          {countryCode ? (
            <>
              <img className="sb-country__flag" src={getFlagSrc(countryCode)} alt="" />
              <span id="site-country-value">{getCountryName(countryCode)}</span>
              <span className="sb-country__code">{countryCode}</span>
            </>
          ) : (
            <span className="sb-country__placeholder" id="site-country-value">{t('Select a country…')}</span>
          )}
          <span className="sb-country__chevron" aria-hidden="true">▾</span>
        </button>
        {countryDropdownOpen && (
          <div className="sb-country__popout">
            <input
              type="text"
              value={countrySearch}
              onChange={(event) => { setCountrySearch(event.target.value); setCountryActiveIndex(-1); }}
              onKeyDown={handleCountryKeyDown}
              placeholder={t('Search…')}
              className="sb-country__search"
              aria-label={t('Search…')}
              autoComplete="off"
              autoFocus
            />
            <div className="sb-country__list">
              {filteredCountries.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  className={`sb-country__item${index === countryActiveIndex ? ' sb-country__item--active' : ''}`}
                  onMouseDown={() => applyCountry(country.code)}
                >
                  <img className="sb-country__flag" src={getFlagSrc(country.code)} alt="" />
                  <span className="sb-country__name">{getCountryName(country.code)}</span>
                  <span className="sb-country__code">{country.code}</span>
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <div className="sb-country__empty">{t('No results')}</div>
              )}
            </div>
          </div>
        )}
        <p className="sb-hint">
          {client && countryCode === client.country_code
            ? t('Taken from {client}. It decides which licence types and timezone apply.', { client: client.name })
            : t('The country decides which licence types and timezone apply.')}
        </p>
      </div>

      {requiresTimeZoneSelection && (
        <div className="sb-field">
          <label className="sb-field__label" htmlFor="site-timezone">{t('Site timezone')}</label>
          <select
            id="site-timezone"
            className="sb-select"
            value={resolvedTimeZone}
            onChange={(event) => setTimezone(event.target.value)}
          >
            {timeZoneOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <p className="sb-hint">
            {t('This country uses multiple timezones, so choose the one used by this site.')}
          </p>
        </div>
      )}

      <AddressSearch
        countryCode={countryCode}
        onSelect={handlePlaceSelected}
        onPickOnMap={() => setShowMapPicker(true)}
      />

      <div className="sb-field">
        <label className="sb-field__label" htmlFor="site-address">{t('Address')}</label>
        <input
          id="site-address"
          type="text"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          className="sb-input sb-input--readonly"
          placeholder={t('Filled in from the search or map pin')}
          readOnly
        />
        <p className={`sb-pinstate${siteCenter ? ' sb-pinstate--set' : ''}`}>
          <span className="sb-pinstate__dot" />
          {siteCenter
            ? t('Pinned at {lat}, {lng}', { lat: latitude!.toFixed(6), lng: longitude!.toFixed(6) })
            : t('No pin yet — you can also drop one on the boundary step.')}
        </p>
      </div>
    </>
  );

  const renderCoverageStep = () => (
    <>
      <div className="sb-choice-grid">
        <button
          type="button"
          className={`sb-choice${is24HourCoverage ? ' sb-choice--on' : ''}`}
          aria-pressed={is24HourCoverage}
          onClick={() => setIs24HourCoverage(true)}
        >
          <strong>{t('24-hour coverage')}</strong>
          <small>{t('The site must be manned around the clock.')}</small>
        </button>
        <button
          type="button"
          className={`sb-choice${!is24HourCoverage ? ' sb-choice--on' : ''}`}
          aria-pressed={!is24HourCoverage}
          onClick={() => setIs24HourCoverage(false)}
        >
          <strong>{t('Fixed window')}</strong>
          <small>{t('Cover runs between a start and end time each day.')}</small>
        </button>
      </div>

      {!is24HourCoverage && (
        <div className="sb-time-grid">
          <div className="sb-field">
            <label className="sb-field__label" htmlFor="coverage-start">{t('Start')}</label>
            <select
              id="coverage-start"
              className="sb-select"
              value={String(coverageStartMinute)}
              onChange={(event) => setCoverageStartMinute(Number.parseInt(event.target.value, 10))}
            >
              {TIME_SELECT_OPTIONS.map((minute) => (
                <option key={`coverage-start-${minute}`} value={minute}>{formatTimeOptionLabel(minute)}</option>
              ))}
            </select>
          </div>
          <div className="sb-field">
            <label className="sb-field__label" htmlFor="coverage-end">{t('End')}</label>
            <select
              id="coverage-end"
              className="sb-select"
              value={String(coverageEndMinute)}
              onChange={(event) => setCoverageEndMinute(Number.parseInt(event.target.value, 10))}
            >
              {COVERAGE_END_OPTIONS.map((minute) => (
                <option key={`coverage-end-${minute}`} value={minute}>{formatTimeOptionLabel(minute)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="sb-field">
        <label className="sb-field__label" htmlFor="site-required-guards">{t('Minimum guards on site *')}</label>
        <input
          id="site-required-guards"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_REQUIRED_GUARDS}
          step={1}
          aria-required="true"
          value={requiredGuardCount}
          onChange={(event) => setRequiredGuardCount(event.target.value)}
          className="sb-input sb-input--count"
        />
        <p className="sb-hint">
          {t('How many guards must be on site at every moment of the coverage window. Auto scheduling drafts a post for each one.')}
        </p>
      </div>

      <div className="sb-summary-card">
        <div className="sb-summary-card__head">
          <strong>{coveragePreview}</strong>
          {isOvernightCoverage && <span className="sb-badge">{t('Overnight')}</span>}
        </div>
        <span>{t('Duty board slots can only be created inside this window. End times earlier than start roll into the next day.')}</span>
      </div>
    </>
  );

  const renderDutyStep = () => (
    <>
      <div className="sb-field">
        <label className="sb-field__label" htmlFor="welfare-mode">{t('Welfare checks')}</label>
        <select
          id="welfare-mode"
          className="sb-select"
          value={welfareCheckMode}
          onChange={(event) => setWelfareCheckMode(event.target.value as SiteWelfareMode)}
        >
          <option value="automatic">{t('Automatic: working alone or at night')}</option>
          <option value="always">{t('Always on for every shift')}</option>
          <option value="off">{t('Off by default')}</option>
        </select>
        <p className="sb-hint">
          {t('Qualifying guards are prompted every 60 minutes and have 10 minutes to check in. Individual shifts can override this default.')}
        </p>
      </div>

      {welfareCheckMode === 'automatic' && (
        <div className="sb-time-grid">
          <div className="sb-field">
            <label className="sb-field__label" htmlFor="welfare-night-start">{t('Night starts')}</label>
            <select
              id="welfare-night-start"
              className="sb-select"
              value={String(welfareNightStartMinute)}
              onChange={(event) => setWelfareNightStartMinute(Number.parseInt(event.target.value, 10))}
            >
              {TIME_SELECT_OPTIONS.map((minute) => (
                <option key={`welfare-night-start-${minute}`} value={minute}>{formatTimeOptionLabel(minute)}</option>
              ))}
            </select>
          </div>
          <div className="sb-field">
            <label className="sb-field__label" htmlFor="welfare-night-end">{t('Night ends')}</label>
            <select
              id="welfare-night-end"
              className="sb-select"
              value={String(welfareNightEndMinute)}
              onChange={(event) => setWelfareNightEndMinute(Number.parseInt(event.target.value, 10))}
            >
              {TIME_SELECT_OPTIONS.map((minute) => (
                <option key={`welfare-night-end-${minute}`} value={minute}>{formatTimeOptionLabel(minute)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="sb-field">
        <label className="sb-field__label" htmlFor="required-licence">{t('Required licence')}</label>
        <select
          id="required-licence"
          className="sb-select"
          value={requiredLicenceTypeId}
          onChange={(event) => setRequiredLicenceTypeId(event.target.value)}
          disabled={!countryCode || isLoadingLicenceTypes}
        >
          {!countryCode && <option value="">{t('Choose a country first')}</option>}
          {countryCode && (
            <option value="">
              {licenceTypes.length > 0 ? t('No licence required') : t('No licence types available')}
            </option>
          )}
          {licenceTypes.map((licenceType) => (
            <option key={licenceType.id} value={licenceType.id}>{licenceType.name}</option>
          ))}
        </select>
        <p className="sb-hint">
          {countryCode
            ? t('This becomes the default licence requirement for duties created at this site.')
            : t('Select a country to load the matching licence types.')}
        </p>
      </div>
    </>
  );

  const renderReviewStep = () => {
    const rows: { step: StepId; label: string; value: string; muted?: boolean }[] = [
      { step: 'location', label: t('Site name'), value: name.trim() },
      { step: 'location', label: t('Address'), value: address.trim() || t('Not set') , muted: !address.trim() },
      {
        step: 'location',
        label: t('Location'),
        value: siteCenter ? `${latitude!.toFixed(6)}, ${longitude!.toFixed(6)}` : t('Not set'),
        muted: !siteCenter,
      },
      {
        step: 'location',
        label: t('Country'),
        value: countryCode ? (getCountryName(countryCode) ?? countryCode) : t('Not set'),
        muted: !countryCode,
      },
      { step: 'location', label: t('Site timezone'), value: resolvedTimeZone },
      { step: 'boundary', label: t('Site geofence'), value: geofencePreview },
      { step: 'coverage', label: t('Required coverage'), value: coveragePreview },
      {
        step: 'coverage',
        label: t('Minimum guards on site'),
        value: parsedGuardCount !== null ? String(parsedGuardCount) : t('Not set'),
        muted: parsedGuardCount === null,
      },
      { step: 'duty', label: t('Welfare checks'), value: welfarePreview },
      {
        step: 'duty',
        label: t('Required licence'),
        value: licenceTypes.find((licenceType) => licenceType.id === requiredLicenceTypeId)?.name
          ?? t('No licence required'),
        muted: !requiredLicenceTypeId,
      },
    ];

    return (
      <div className="sb-review">
        {rows.map((row) => (
          <div key={`${row.step}-${row.label}`} className="sb-review__row">
            <span className="sb-review__label">{row.label}</span>
            <span className={`sb-review__value${row.muted ? ' sb-review__value--muted' : ''}`}>{row.value}</span>
            <button
              type="button"
              className="sb-review__edit"
              onClick={() => goToStep(STEP_IDS.indexOf(row.step))}
            >
              {t('Edit')}
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderStepBody = () => {
    switch (step) {
      case 'location': return renderLocationStep();
      case 'coverage': return renderCoverageStep();
      case 'duty': return renderDutyStep();
      case 'review': return renderReviewStep();
      default: return null;
    }
  };

  // ── Shell ───────────────────────────────────────────────────────────

  const isLastStep = stepIndex === STEP_IDS.length - 1;

  return createPortal(
    <div
      className="site-builder"
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-builder-title"
    >
      <div className={`sb-app${isEditing ? ' sb-app--editing' : ''}`}>
        <header className="sb-top">
          <button
            type="button"
            className="sb-top__brand"
            onClick={handleRequestClose}
            title={t('Back to Sites')}
          >
            <img src={logoSrc} alt="" className="sb-top__logo" />
            <span className="sb-top__title-stack">
              <span className="sb-top__title" id="site-builder-title">
                {isEditing ? t('Edit site') : t('Create new site')}
              </span>
              <span className="sb-top__sub">{isEditing ? site.name : client?.name}</span>
            </span>
          </button>

          <ol className="sb-stepper" aria-label={t('Site setup steps')}>
            {STEP_IDS.map((stepId, index) => {
              // A tick means "you completed this". Editing starts complete, so
              // the other steps read as plain, reachable sections instead.
              const state = index === stepIndex
                ? 'current'
                : isEditing
                  ? 'available'
                  : index < furthestStepIndex ? 'done' : 'todo';
              return (
                <li key={stepId} className={`sb-stepper__item sb-stepper__item--${state}`}>
                  <button
                    type="button"
                    className="sb-stepper__btn"
                    aria-current={index === stepIndex ? 'step' : undefined}
                    disabled={index > furthestStepIndex}
                    onClick={() => goToStep(index)}
                  >
                    <span className="sb-stepper__marker">
                      {state === 'done' ? (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                      ) : index + 1}
                    </span>
                    <span className="sb-stepper__label">{getStepLabel(stepId)}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="sb-top__actions">
            <button type="button" className="sb-btn sb-btn--ghost" onClick={handleRequestClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
              {t('Back to Sites')}
            </button>
            {/* Editing can be finished from any step, so Save lives in the topbar. */}
            {isEditing && (
              <button
                type="button"
                className="sb-btn sb-btn--primary"
                onClick={() => { void handleSave(); }}
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? t('Saving...') : t('Save changes')}
              </button>
            )}
          </div>
        </header>

        {/* Every step validates itself, with its own message, rather than the browser's. */}
        <form className="sb-form" onSubmit={handleStepSubmit} noValidate>
          <main className={`sb-body${isMapStep ? ' sb-body--map' : ''}`}>
            {isMapStep ? (
              <>
                <SiteBoundaryCanvas
                  mode={geofenceMode}
                  onModeChange={setGeofenceMode}
                  radiusMetres={geofenceRadius}
                  onRadiusChange={setGeofenceRadius}
                  minRadius={MIN_SITE_GEOFENCE_RADIUS_M}
                  maxRadius={MAX_SITE_GEOFENCE_RADIUS_M}
                  points={geofencePolygonPoints}
                  onPointsChange={setGeofencePolygonPoints}
                  siteCenter={siteCenter}
                  onSiteCenterChange={handleSiteCenterChange}
                  siteName={name || undefined}
                />
                <h2 className="sb-visually-hidden" ref={headingRef} tabIndex={-1}>
                  {getStepTitle('boundary', isEditing)}
                </h2>
              </>
            ) : (
              <div className="sb-step">
                <div className="sb-step__head">
                  <span className="sb-step__eyebrow">
                    {t('Step {current} of {total}', { current: stepIndex + 1, total: STEP_IDS.length })}
                  </span>
                  <h2 className="sb-step__title" ref={headingRef} tabIndex={-1}>{getStepTitle(step, isEditing)}</h2>
                  <p className="sb-step__hint">{getStepHint(step, isEditing)}</p>
                </div>
                <div className="sb-step__body">{renderStepBody()}</div>
              </div>
            )}
          </main>

          <footer className="sb-foot">
            <div className="sb-foot__status" role="status" aria-live="polite">
              {error ? (
                <span className="sb-foot__error">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16.5v.01" /></svg>
                  {error}
                </span>
              ) : (
                <span className="sb-foot__hint">
                  {isMapStep ? getStepHint('boundary', isEditing) : t('Step {current} of {total}', { current: stepIndex + 1, total: STEP_IDS.length })}
                </span>
              )}
            </div>

            <div className="sb-foot__actions">
              <button
                type="button"
                className="sb-btn sb-btn--ghost"
                onClick={() => goToStep(stepIndex - 1)}
                disabled={stepIndex === 0 || isSubmitting}
              >
                {t('Back')}
              </button>
              {isLastStep ? (
                <button type="submit" className="sb-btn sb-btn--primary" disabled={isSubmitting}>
                  {isEditing
                    ? (isSubmitting ? t('Saving...') : t('Save changes'))
                    : (isSubmitting ? t('Creating...') : t('Create Site'))}
                </button>
              ) : (
                <button type="submit" className="sb-btn sb-btn--primary">
                  {t('Continue')}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </button>
              )}
            </div>
          </footer>
        </form>
      </div>

      {showMapPicker && (
        <MapPickerModal onConfirm={handleMapConfirm} onClose={() => setShowMapPicker(false)} />
      )}

      {exitConfirmPending && (
        <div className="sb-exit">
          <div className="sb-exit__card" role="alertdialog" aria-labelledby="sb-exit-title">
            <h3 id="sb-exit-title">{isEditing ? t('Discard your changes?') : t('Discard this site?')}</h3>
            <p>
              {isEditing
                ? t('This site will keep its saved details.')
                : tn('{count} step of detail will be lost.', '{count} steps of detail will be lost.', furthestStepIndex + 1)}
            </p>
            <div className="sb-exit__actions">
              <button type="button" className="sb-btn sb-btn--ghost" onClick={() => setExitConfirmPending(false)}>
                {t('Keep editing')}
              </button>
              <button type="button" className="sb-btn sb-btn--danger" onClick={onClose}>
                {t('Discard')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
